import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {ColorSwatchIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {MembershipPlan} from "types/MembershipPlan";
import Dropdown from "components/Dropdown";
import DeleteMembershipPlan from '../Delete';
import TicketingSource from "types/TicketingSource";
import useMoney from "../../../hooks/useMoney";
import {useOperator} from "../../../context/OperatorContext";

function MembershipPlanList() {
    const {termFor} = useOperator();
    const {formattedMoney} = useMoney();

    const [page, setPage] = useState<number>(1);
    const result = useQuery(['membership-plans.all', {page}], () => ngage.membershipPlans.all(page), {keepPreviousData: true})

    const [deletingMembershipPlan, setDeletingMembershipPlan] = useState<MembershipPlan | null>(null);

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prefetch next and previous pages
    prefetchNearbyPages('membership-plans.all', page, ngage.membershipPlans.all)

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/membership-plans/new')}>New</Button>

    return (
        <Page title={termFor('Membership Plans')} actions={actions} breadcrumbs={{'Commercial': '/', [termFor('Membership Plans')]: '/membership-plans'}}>
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Description</TableHeader>
                        <TableHeader>Duration</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>Price</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>{termFor('Memberships')}</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={6}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <ColorSwatchIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No membership plans</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((membershipPlan, index) => (
                        <TableRow key={membershipPlan.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position='left'>
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/membership-plans/${membershipPlan.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/membership-plans/${membershipPlan.id}/edit`), disabled: membershipPlan.source.name !== TicketingSource.NGAGE},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingMembershipPlan(membershipPlan), disabled: (membershipPlan.source.name !== TicketingSource.NGAGE) || (membershipPlan.membership_count > 0)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <ColorSwatchIcon className="h-5 w-5 mr-2 text-gray-500" /> {membershipPlan.name}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{truncate(membershipPlan.description, 50)}</TableData>
                            <TableData>{membershipPlan.duration.toHuman()}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>{membershipPlan.price? formattedMoney(membershipPlan.price, membershipPlan.currency) : 'Free'}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>{membershipPlan.membership_count?.toLocaleString()}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/membership-plans/${membershipPlan.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/membership-plans/${membershipPlan.id}/edit`), disabled: membershipPlan.source.name !== TicketingSource.NGAGE},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingMembershipPlan(membershipPlan), disabled: (membershipPlan.source.name !== TicketingSource.NGAGE) || (membershipPlan.membership_count > 0)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete confirmation modal */}
                            <DeleteMembershipPlan
                                membershipPlan={deletingMembershipPlan}
                                show={membershipPlan === deletingMembershipPlan}
                                onCancel={() => setDeletingMembershipPlan(null)}
                                onDelete={membershipPlan => {
                                    navigate('/membership-plans')
                                    notify("success", "Membership Plan Deleted", `${membershipPlan.name} has been permanently removed`)
                                }}
                                onDeleteFail={membershipPlan => {
                                    setDeletingMembershipPlan(null)
                                    notify("error", "Membership Plan Not Deleted", `${membershipPlan.name} could not be removed`)
                                }}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default MembershipPlanList;